import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Info, HeartPulse, AlertCircle, HelpCircle } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/Card';
import { getCchiPanel } from '../../../../api/Payroll/cchi';
import { getApiErrorMessage } from '../../../../utils/apiErrors';
import type { CchiPanelData, CchiCoverageRow } from '../../../../models/payroll';

export interface KsaCchiPanelProps {
  onError: (msg: string) => void;
}

type StatusFilter = 'all' | CchiCoverageRow['status'];
type VisaFilter = 'all' | string;

export const KsaCchiPanel: React.FC<KsaCchiPanelProps> = ({ onError }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<CchiPanelData | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [visaFilter, setVisaFilter] = useState<VisaFilter>('all');

  const fetchData = useCallback(async () => {
    setSearch('');
    setStatusFilter('all');
    setVisaFilter('all');
    setLoading(true);
    try {
      const result = await getCchiPanel();
      if (result.errors?.length) {
        onError(getApiErrorMessage(result, t, t('common.error')));
        return;
      }
      setData(result.payload ?? null);
    } catch (e) {
      onError(getApiErrorMessage(e, t, t('common.error')));
    } finally {
      setLoading(false);
    }
  }, [onError, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-5 text-sm text-muted-foreground">{t('common.loading')}</CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const visaCategories = Array.from(new Set(data.coverage.map(c => c.visaCategory).filter(Boolean))) as string[];

  const filtered = data.coverage.filter(row => {
    if (search && !row.employeeName.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== 'all' && row.status !== statusFilter) return false;
    if (visaFilter !== 'all' && row.visaCategory !== visaFilter) return false;
    return true;
  });

  const statusBadgeClass = (s: CchiCoverageRow['status']) => {
    if (s === 'active') return 'bg-emerald-100 text-emerald-800';
    if (s === 'expiringIn7') return 'bg-red-100 text-red-800';
    if (s === 'expiringIn30') return 'bg-orange-100 text-orange-800';
    if (s === 'expiringIn60') return 'bg-amber-100 text-amber-800';
    if (s === 'expired') return 'bg-red-200 text-red-900';
    if (s === 'waived') return 'bg-slate-200 text-slate-700';
    return 'bg-yellow-100 text-yellow-800';
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent
          className="p-4 flex items-start gap-3 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900"
          role="note"
        >
          <Info className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-900 dark:text-amber-200">
            {t('payroll.complianceCenter.cchi.disclaimer')}
          </p>
        </CardContent>
      </Card>

      <Card className="border-rose-100/60 bg-rose-50/30">
        <CardContent className="p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <HeartPulse className="w-4 h-4" />
            {t('payroll.complianceCenter.cchi.title', 'CCHI Coverage')}
          </h3>

          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-4">
            <SummaryChip label={t('payroll.complianceCenter.cchi.summary.total')} value={data.totalNonSaudisCount} />
            <SummaryChip label={t('payroll.complianceCenter.cchi.summary.active')} value={data.activeCount} variant="emerald" />
            <SummaryChip label={t('payroll.complianceCenter.cchi.summary.expiring7')} value={data.expiringIn7Count} variant="red" />
            <SummaryChip label={t('payroll.complianceCenter.cchi.summary.expiring30')} value={data.expiringIn30Count} variant="orange" />
            <SummaryChip label={t('payroll.complianceCenter.cchi.summary.expiring60')} value={data.expiringIn60Count} variant="amber" />
            <SummaryChip label={t('payroll.complianceCenter.cchi.summary.expired')} value={data.expiredCount} variant="red" />
            <SummaryChip label={t('payroll.complianceCenter.cchi.summary.missing')} value={data.missingCount} variant="yellow" />
            <SummaryChip label={t('payroll.complianceCenter.cchi.summary.waived')} value={data.waivedCount} variant="slate" />
          </div>

          <div className="flex flex-wrap items-end gap-2 mb-3">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('common.search')}
              className="rounded border bg-background px-3 py-2 text-sm flex-1 min-w-[180px]"
            />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as StatusFilter)}
              aria-label={t('payroll.complianceCenter.cchi.filters.statusLabel')}
              className="rounded border bg-background px-3 py-2 text-sm"
            >
              <option value="all">{t('common.allStatuses')}</option>
              <option value="active">{t('payroll.cchi.status.active')}</option>
              <option value="expiringIn7">{t('payroll.cchi.status.expiringIn7')}</option>
              <option value="expiringIn30">{t('payroll.cchi.status.expiringIn30')}</option>
              <option value="expiringIn60">{t('payroll.cchi.status.expiringIn60')}</option>
              <option value="expired">{t('payroll.cchi.status.expired')}</option>
              <option value="missing">{t('payroll.cchi.status.missing')}</option>
              <option value="waived">{t('payroll.cchi.status.waived')}</option>
            </select>
            <select
              value={visaFilter}
              onChange={e => setVisaFilter(e.target.value)}
              aria-label={t('payroll.complianceCenter.cchi.filters.visaCategoryLabel')}
              className="rounded border bg-background px-3 py-2 text-sm"
            >
              <option value="all">{t('common.allVisaCategories')}</option>
              {visaCategories.map(c => (
                <option key={c} value={c}>{t(`profile.personal.visaCategoryOptions.${c}`, c)}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={fetchData}
              className="rounded border bg-background px-3 py-2 text-sm hover:bg-muted"
            >
              {t('common.refresh')}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-muted-foreground">
                  <th className="text-start py-2 font-medium">{t('payroll.complianceCenter.cchi.table.employee')}</th>
                  <th className="text-start py-2 font-medium">{t('payroll.complianceCenter.cchi.table.visaCategory')}</th>
                  <th className="text-start py-2 font-medium">{t('payroll.complianceCenter.cchi.table.tier')}</th>
                  <th className="text-start py-2 font-medium">{t('payroll.complianceCenter.cchi.table.expiryDate')}</th>
                  <th className="text-start py-2 font-medium">{t('payroll.complianceCenter.cchi.table.status')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(row => (
                  <tr key={row.userId} className="border-b border-border last:border-0">
                    <td className="py-2">
                      <Link to={`/profile/${encodeURIComponent(row.userId)}/payroll/benefits#cchi`} className="text-primary hover:underline">
                        {row.employeeName}
                      </Link>
                    </td>
                    <td className="py-2">
                      {row.visaCategory ? t(`profile.personal.visaCategoryOptions.${row.visaCategory}`, row.visaCategory) : '—'}
                    </td>
                    <td className="py-2 flex items-center gap-1">
                      {row.planTier ? t(`payroll.cchi.planTier.${row.planTier}`) : '—'}
                      {row.expectedTier && row.planTier && row.expectedTier !== row.planTier && (
                        <span title={t('payroll.cchi.editor.tierMismatch', { expectedTier: row.expectedTier, actualTier: row.planTier })}>
                          <AlertCircle className="w-4 h-4 text-amber-600" />
                        </span>
                      )}
                      {/* CO-269 — the expected tier could NOT be computed for this employee. Muted,
                          not amber/red: unknown is not a finding, and this must not read as a breach. */}
                      {row.expectedTierUnknown && (
                        <span
                          data-testid="cchi-expected-tier-unknown-row"
                          title={`${t('payroll.cchi.expectedTierUnknown')} — ${t('payroll.cchi.expectedTierUnknownHint')}`}
                          aria-label={t('payroll.cchi.expectedTierUnknown')}
                        >
                          <HelpCircle className="w-4 h-4 text-muted-foreground" />
                        </span>
                      )}
                    </td>
                    <td className="py-2">{row.expiryDate?.slice(0, 10) ?? '—'}</td>
                    <td className="py-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadgeClass(row.status)}`}>
                        {t(`payroll.cchi.status.${row.status}`)}
                      </span>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-4 text-center text-muted-foreground">{t('common.noData')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

function SummaryChip({ label, value, variant }: { label: string; value: number; variant?: 'emerald' | 'red' | 'orange' | 'amber' | 'yellow' | 'slate' }) {
  const tone =
    variant === 'emerald' ? 'bg-emerald-50 text-emerald-800' :
    variant === 'red'     ? 'bg-red-50 text-red-800' :
    variant === 'orange'  ? 'bg-orange-50 text-orange-800' :
    variant === 'amber'   ? 'bg-amber-50 text-amber-800' :
    variant === 'yellow'  ? 'bg-yellow-50 text-yellow-800' :
    variant === 'slate'   ? 'bg-slate-50 text-slate-800' :
                            'bg-muted text-foreground';
  return (
    <div className={`rounded-lg p-2 text-center ${tone}`}>
      <div className="text-xs">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}
